import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { BellRing, Check, Eye, EyeOff } from 'lucide-react';
import { Button, ErrorState, Skeleton, StatusBadge, AdminPage } from '@/design/components';
import { supabase } from '@/lib/supabase';
import { usePerm } from '@/access/usePerm';
import { formatDateTime } from '@/lib/dates';
import { errorMessage } from '@/lib/errors';

type Row = {
  employee_id: string;
  full_name: string;
  read_at: string | null;
  acknowledged_at: string | null;
};

/** דוח אישורי קריאה (PRD 5.3.3): מי אישר, מי קרא ולא אישר, מי לא פתח. */
export function AckReportPage() {
  const { messageId = '' } = useParams();
  const canRead = usePerm('messages.read_reports');
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const report = useQuery({
    queryKey: ['ack-report', messageId],
    queryFn: async (): Promise<Row[]> => {
      const { data, error: failure } = await supabase.rpc('unread_ack_report', {
        p_message: messageId,
      });
      if (failure) throw failure;
      return (data ?? []) as Row[];
    },
  });

  const message = useQuery({
    queryKey: ['report-message', messageId],
    queryFn: async () => {
      const { data, error: failure } = await supabase
        .from('messages')
        .select('id, body, created_at, requires_ack, channels ( name )')
        .eq('id', messageId)
        .single();
      if (failure) throw failure;
      return data;
    },
  });

  const remind = useMutation({
    mutationFn: async () => {
      const { data, error: failure } = await supabase.rpc('remind_unacked', {
        p_message: messageId,
      });
      if (failure) throw failure;
      return data as unknown as number;
    },
    onSuccess: (count) => {
      setError(null);
      setNotice(count > 0 ? `נשלחה תזכורת ל ${count} עובדים.` : 'כולם כבר אישרו. לא נשלחה תזכורת.');
      void report.refetch();
    },
    onError: (caught) => {
      setNotice(null);
      setError(errorMessage(caught));
    },
  });

  if (!canRead) return <Navigate to="/admin" replace />;

  const rows = report.data ?? [];
  const acked = rows.filter((row) => row.acknowledged_at !== null);
  const readOnly = rows.filter((row) => row.acknowledged_at === null && row.read_at !== null);
  const untouched = rows.filter((row) => row.read_at === null);

  return (
    <AdminPage
      backTo="/admin/channels"
      title="דוח אישורי קריאה"
      subtitle={message.data?.channels?.name ?? undefined}
    >
      {message.data?.body && (
        <blockquote className="m-0 rounded-md border-s-4 border-charcoal bg-raised p-3 text-body">
          {message.data.body}
          <span className="mt-1 block text-body-sm text-muted">
            נשלחה {formatDateTime(message.data.created_at)}
          </span>
        </blockquote>
      )}

      {report.isPending && <Skeleton className="h-24 w-full" />}
      {report.isError && <ErrorState onRetry={() => void report.refetch()} />}

      {report.data && (
        <>
          <div className="flex flex-wrap gap-2">
            <StatusBadge tone="good">{acked.length} אישרו</StatusBadge>
            <StatusBadge tone="warn">{readOnly.length} קראו ולא אישרו</StatusBadge>
            <StatusBadge tone="bad">{untouched.length} לא פתחו</StatusBadge>
          </div>

          {notice && (
            <p role="status" className="m-0 text-body-sm text-good">
              {notice}
            </p>
          )}
          {error && (
            <p role="alert" className="m-0 text-body-sm text-bad">
              {error}
            </p>
          )}

          {message.data?.requires_ack && (
            <div>
              <Button
                variant="primary"
                disabled={remind.isPending || acked.length === rows.length}
                onClick={() => remind.mutate()}
              >
                <BellRing size={20} strokeWidth={1.75} aria-hidden="true" />
                שליחת תזכורת למי שלא אישר
              </Button>
            </div>
          )}

          <Group title="אישרו" tone="good" rows={acked} icon={Check} />
          <Group title="קראו ולא אישרו" tone="warn" rows={readOnly} icon={Eye} />
          <Group title="לא פתחו" tone="bad" rows={untouched} icon={EyeOff} />
        </>
      )}
    </AdminPage>
  );
}

function Group({
  title,
  tone,
  rows,
  icon: Icon,
}: {
  title: string;
  tone: 'good' | 'warn' | 'bad';
  rows: Row[];
  icon: typeof Check;
}) {
  if (rows.length === 0) return null;

  return (
    <section className="grid gap-2">
      <h2 className="m-0 flex items-center gap-2 font-display text-display-m font-semibold">
        <Icon size={20} strokeWidth={1.75} aria-hidden="true" />
        {title} ({rows.length})
      </h2>
      <ul className="grid list-none gap-1 p-0">
        {rows.map((row) => (
          <li
            key={row.employee_id}
            className="flex flex-wrap items-center justify-between gap-2 border-t border-line py-2 text-body"
          >
            <span>{row.full_name}</span>
            <span className="text-body-sm text-muted">
              {row.acknowledged_at ? (
                `אישר ב ${formatDateTime(row.acknowledged_at)}`
              ) : row.read_at ? (
                `קרא ב ${formatDateTime(row.read_at)}`
              ) : (
                <StatusBadge tone={tone}>לא נפתח</StatusBadge>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
